<script setup>
import {useReservationsStore, useRestaurantsStore, useUserGroupsStore, useUsersStore} from '@/stores'
import { useDesignSystem } from '@/composables/useDesignSystem'
import moment from 'moment'
import Tooltip from '@/layouts/components/Tooltip.vue'
import AppDateTimePicker from '@core/components/AppDateTimePicker.vue'
import {
  emailValidator,
  requiredValidator,
} from '@validators'

const emit = defineEmits([
  'update:modelValue',
  'submit',
  'update:isDialogVisible'
])

const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth('md')

const usersStore = useUsersStore()
let myUser = null;
({ myUser } = storeToRefs(usersStore))

const userGroupsStore = useUserGroupsStore()
let adminEditingUserGroupMembers = null;
({ adminEditingUserGroupMembers } = storeToRefs(userGroupsStore))

const isDialogVisible = ref(false)
const isConfirmDialogOpen = ref(false)
const isConfirmRemindDialogOpen = ref(false)
const isConfirmConnectAllOpen = ref(false)

const validInputs = ref(true)

const tab = ref(null)
const searchValue = ref("")

const form = ref()
const valid = ref(true)

const origin = ref(window.location.origin)

const onFormSubmit = async () => {
  form.value.validate().then(({valid: isValid}) => {
    if(isValid){
      userGroupsStore.saveAdminEditingUserGroupMembers()
      emit('update:isDialogVisible', false)
    }
  })
}


// "Connect all members" toggle. Enabling auto-connects every member to each
// other (bi-directional follow, no notification); it's persisted on the group
// so members added later are auto-connected and removed members disconnected.
// The switch is one-way bound to the persisted value, so cancelling the confirm
// leaves it visually off until actually saved.
const onToggleConnectAll = val => {
  if (val) {
    isConfirmConnectAllOpen.value = true
  } else {
    adminEditingUserGroupMembers.value.autoConnectMembers = false
    userGroupsStore.saveAdminEditingUserGroupMembers()
  }
}

const confirmConnectAll = () => {
  adminEditingUserGroupMembers.value.autoConnectMembers = true
  userGroupsStore.saveAdminEditingUserGroupMembers()
}

const remindAllNeedsToRegister = () => {

  //for every filteredMembersNeedToRegister send a reminder
  for(let member of filteredMembersNeedToRegister.value){
    member.sendInvite = true
    member.invitedBy = myUser.email
  }
  userGroupsStore.saveAdminEditingUserGroupMembers()
}


//check to see if we should pop up any dialogs when the store changes
//this seems hacky
const router = useRouter()
userGroupsStore.$subscribe((mutation, state) => {
  // Don't open dialog when on the group-members page (it manages members inline)
  if (router.currentRoute.value.path === '/group-members') return
  isDialogVisible.value = ( state.adminEditingUserGroupMembers != null)
})

const requestClose = () => {
  userGroupsStore.cancelAdminEditUserGroupMembers()
  isDialogVisible.value = false
  searchValue.value = ''
}


const exportUserPermissions = function() {

  const items = adminEditingUserGroupMembers?.value?.userPermissions

  // let itemWithMostKeys = {};
  // for(let item of items){
  //   if(Object.keys(item).length > Object.keys(itemWithMostKeys).length){
  //     itemWithMostKeys = item
  //   }
  // }

  const replacer = (key, value) => {
    //nulls, arrays, objects all export as empty string
    if(value === null || typeof value === "array" || typeof value === "object"){
      return ''
    }
    
    return value
  }

  const header = ['firstName', 'lastName', 'email']
  const csv = [
    'First Name, Last Name, Email', // header row first
    ...items.map(row => header.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(','))
  ].join('\r\n')


  // The download function takes a CSV string, the filename and mimeType as parameters
  // Scroll/look down at the bottom of this snippet to see how download is called
  var download = function(content, fileName, mimeType) {
    var a = document.createElement('a')
    mimeType = mimeType || 'application/octet-stream'

    if (navigator.msSaveBlob) { // IE10
      navigator.msSaveBlob(new Blob([content], {
        type: mimeType
      }), fileName)
    } else if (URL && 'download' in a) { //html5 A[download]
      a.href = URL.createObjectURL(new Blob([content], {
        type: mimeType
      }))
      a.setAttribute('download', fileName)
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } else {
      location.href = 'data:application/octet-stream,' + encodeURIComponent(content) // only this mime type is supported
    }
  }

  download(csv, `${adminEditingUserGroupMembers.value.name} Members_${moment().format('YYYY-MM-DD')}.csv`, 'text/csv;encoding:utf-8')

}



function customSort(a, b) {
  // Check isNew property and prioritize items with isNew set to true
  if (a.isNew && !b.isNew) {
    return -1
  } else if (!a.isNew && b.isNew) {
    return 1
  }
  const hasNonNullValues = obj => {
    return obj.firstName !== null && obj.lastName !== null && obj.company !== null &&
      obj.title !== null && obj.email !== null && obj.phoneNumber !== null
  }

  const compareByFirstNameAndEmail = (a, b) => {
    if (a.firstName && b.firstName) {
      if (a.firstName === b.firstName) {
        return a.email.localeCompare(b.email)
      }
      
      return a.firstName.localeCompare(b.firstName)
    } else if (!a.firstName && !b.firstName && a.email && b.email ) {
      return a.email.localeCompare(b.email)
    } else if (!a.firstName) {
      return 1
    } else {
      return -1
    }
  }

  if (a.emailConfirmed && b.emailConfirmed) {
    const aHasNonNullValues = hasNonNullValues(a)
    const bHasNonNullValues = hasNonNullValues(b)

    if (aHasNonNullValues && bHasNonNullValues) {
      return compareByFirstNameAndEmail(a, b)
    } else if (aHasNonNullValues && !bHasNonNullValues) {
      return -1
    } else if (!aHasNonNullValues && bHasNonNullValues) {
      return 1
    } else {
      return compareByFirstNameAndEmail(a, b)
    }
  } else if (a.emailConfirmed && !b.emailConfirmed) {
    return -1
  } else if (!a.emailConfirmed && b.emailConfirmed) {
    return 1
  } else {
    return compareByFirstNameAndEmail(a, b)
  }
}

const membersSearchedAndSorted = computed(() => {
  var returnArray = adminEditingUserGroupMembers?.value?.userPermissions || []

  // Process search input
  if (searchValue.value != '' && searchValue.value && returnArray) {
    returnArray = returnArray.filter(item => {
      return item?.email
        ?.toUpperCase()
        ?.includes(searchValue.value.toUpperCase()) ||
        item?.firstName
          ?.toUpperCase()
          ?.includes(searchValue.value.toUpperCase()) ||
        item?.lastName
          ?.toUpperCase()
          ?.includes(searchValue.value.toUpperCase()) ||
        item?.permission
          ?.toUpperCase()
          ?.includes(searchValue.value.toUpperCase())
    })
  }

  returnArray.sort(customSort)
  
  return returnArray
})


const newMember = computed(() => {
  var returnArray = adminEditingUserGroupMembers?.value?.userPermissions?.filter(item =>
    (item.isNew)
  ) 
  
  return returnArray?.[0] || null
})

const filteredMembersActive = computed(() => {
  return membersSearchedAndSorted?.value?.filter(item =>
    (item?.emailConfirmed && item.permission != 'pending'  && !item.isNew)
  ) || []
})

const filteredMembersNeedToRegister = computed(() => {
  return membersSearchedAndSorted?.value?.filter(item =>
    (!item?.emailConfirmed && item.permission != 'pending'  && !item.isNew)
  ) || []
})

const filteredMembersPending = computed(() => {
  return membersSearchedAndSorted?.value?.filter(item =>
    (item.permission == 'pending'  && !item.isNew )
  ) || []
})

const membershipsActiveCount = computed(() => {
  return adminEditingUserGroupMembers?.value?.userPermissions?.filter(item =>
    (item?.emailConfirmed && item.permission != 'pending' && item.permission != 'adminStandard'  && !item.isNew)
  ).length || 0
})

const membershipsRemainingCount = computed(() => {
  let diff = adminEditingUserGroupMembers?.value?.currentNumberOfPaidSeats - membershipsActiveCount?.value
  
  return (diff > 0 ? diff : 0)
})

const seatPriceDollars = ref(100)

const platinumAccountName = computed(() => {
  if(adminEditingUserGroupMembers?.value?.currentNumberOfPaidSeats >= 33){
    seatPriceDollars.value = 100
    
    return "Platinum 50"
  }
  if(adminEditingUserGroupMembers?.value?.currentNumberOfPaidSeats >= 5){
    seatPriceDollars.value = 150
    
    return "Platinum 5"
  }
  if(adminEditingUserGroupMembers?.value?.currentNumberOfPaidSeats >= 1){
    seatPriceDollars.value = 250
    
    return "Platinum 1"
  }
})



const scrollToTop = () => {
  const mainContent = document.querySelector('.main-content')
  if (mainContent) {
    mainContent.scrollTop = 0
  }
}
</script>

<template>
  <ConfirmDialog
    v-model:is-dialog-visible="isConfirmDialogOpen"
    confirmation-msg="Save changes?"
    confirm-btn-text="Yes"
    cancel-btn-text="No"
    @confirm="userGroupsStore.saveAdminEditingUserGroupMembers(); isDialogVisible = false; searchValue = '';"
    @cancel="userGroupsStore.cancelAdminEditUserGroupMembers(); isDialogVisible = false; searchValue = '';"
  />
  <ConfirmDialog
    v-model:is-dialog-visible="isConfirmRemindDialogOpen"
    confirmation-msg="Send email reminders to all members who have not registered?"
    confirm-btn-text="Yes"
    cancel-btn-text="No"
    @confirm="remindAllNeedsToRegister"
  />
  <ConfirmDialog
    v-model:is-dialog-visible="isConfirmConnectAllOpen"
    confirmation-msg="Connect all members of this group to each other? They'll follow each other (no notification sent). Members added later are auto-connected, and removed members are disconnected."
    confirm-btn-text="Yes"
    cancel-btn-text="No"
    @confirm="confirmConnectAll"
  />
  <VDialog
    v-if="adminEditingUserGroupMembers"
    v-model="isDialogVisible"
    :width="dWidth"
    persistent
    fullscreen
  >
    <VToolbar
      dark
      color="primary"
      class="toolbar-header"
    >
      <VToolbarItems style="width: 58px" />
      <VSpacer />
      <VToolbarTitle v-if="adminEditingUserGroupMembers.type=='friends'">
        Friends
      </VToolbarTitle>
      <VToolbarTitle v-else>
        Members
      </VToolbarTitle>
      <VSpacer />
      <VToolbarItems>
        <VBtn
          icon="$close"
          size="large"
          variant="text"
          @click="requestClose"
        >
          <VIcon>mdi-close</VIcon>
        </VBtn>
      </VToolbarItems>
    </VToolbar>
    <div class="info-header ">
      <div
        class="float-right position-absolute"
        style="right: -2px; top: -2px;"
      >
        <VTooltip text="Download all members to csv">
          <template #activator="{ props }">
            <VBtn
              class="export-btn"
              color="secondary"
              icon="mdi-file-export"
              variant="text"
              v-bind="props"
              @click="exportUserPermissions"
            />
          </template>
        </VTooltip>
      </div>
      <VRow class="ma-0">
        <VCol cols="auto">
          <!--          <v-row no-gutters align="center" style="margin-top: -5px !important;" v-if="adminEditingUserGroupMembers.publicId" > -->
          <!--            <v-col cols="auto"> -->
          <!--              <a class="text-caption" :href="`${origin}${adminEditingUserGroupMembers.joinUrl}`" target="_blank">{{`reserverm.com${adminEditingUserGroupMembers.joinUrl}`}}</a> -->
          <!--            </v-col> -->
          <!--            <v-col cols="auto"> -->
          <!--              <v-icon class="raised-icon" color="primary"  size="x-small">mdi-open-in-new</v-icon> -->
          <!--            </v-col> -->
          <!--          </v-row> -->
          <div v-if="adminEditingUserGroupMembers.type=='enterprise'">
            <span class="text-h5"><b>{{ adminEditingUserGroupMembers.name }} - <RouterLink
              :to="{ name: 'membership' }"
              @click=" requestClose"
            >Platinum</RouterLink></b></span><br>
            <div>
              Paid memberships: {{ adminEditingUserGroupMembers.currentNumberOfPaidSeats }}<br>
              <span>Active: {{ membershipsActiveCount }}<br></span>
              <span v-if="membershipsRemainingCount > 0">Remaining: {{ membershipsRemainingCount }}</span>
              <span v-if="membershipsRemainingCount == 0">add more: $400/each<br><span class="text-caption">(billed to you once active)<br>group rates available for 5+<br></span></span>
            </div>
          </div>
          <div v-else>
            <span class="text-h5"><b>{{ adminEditingUserGroupMembers.name }}</b></span><br>
            <span
              v-if="adminEditingUserGroupMembers.type == 'restaurant' && adminEditingUserGroupMembers?.restaurant?.market"
              class="text-sm"
            ><b>{{ adminEditingUserGroupMembers.restaurant.market.split(',')[0] }}</b></span><br>
          </div>
          <VSwitch
            v-if="myUser?.userLevel == 'admin'"
            :model-value="!!adminEditingUserGroupMembers.autoConnectMembers"
            label="Connect all members"
            color="primary"
            density="compact"
            hide-details
            class="mt-1"
            @update:model-value="onToggleConnectAll"
          />
          <template v-if="!newMember && (myUser?.userLevel == 'admin' || myUser?.primaryGroupPermission == 'admin' || myUser?.primaryGroupPermission == 'adminStandard' || myUser?.primaryGroupPermission == 'owner')">
            <VTooltip text="Add New Member">
              <template #activator="{ props }">
                <VBtn
                  v-if="adminEditingUserGroupMembers.type == 'restaurant'"
                  class="mt-2"
                  color="primary"
                  v-bind="props"
                  @click="adminEditingUserGroupMembers.userPermissions.push({email: '', permission: 'admin', isNew: true, invitedBy:myUser.email});"
                >
                  <VIcon class="mr-1">
                    mdi-add-circle-outline
                  </VIcon> Member
                </VBtn>
                <VBtn
                  v-if="adminEditingUserGroupMembers.currentNumberOfPaidSeats == 1"
                  class="mt-2"
                  color="primary"
                  v-bind="props"
                  @click="adminEditingUserGroupMembers.userPermissions.push({email: '', permission: 'adminStandard', isNew: true, invitedBy:myUser.email});"
                >
                  <VIcon class="mr-1">
                    mdi-add-circle-outline
                  </VIcon> Member
                </VBtn>
                <VBtn
                  v-if="adminEditingUserGroupMembers.currentNumberOfPaidSeats > 1"
                  class="mt-2"
                  color="primary"
                  v-bind="props"
                  @click="adminEditingUserGroupMembers.userPermissions.push({email: '', permission: 'regular', isNew: true, invitedBy:myUser.email});"
                >
                  <VIcon class="mr-1">
                    mdi-add-circle-outline
                  </VIcon> Member
                </VBtn>
              </template>
            </VTooltip>
          </template>
        </VCol>
      </VRow>

      <GroupMemberCardAdmin
        v-if="newMember"
        class="mb-4"
        style="width: 100%;"
        :member="newMember"
        :user-group="adminEditingUserGroupMembers"
        :valid-inputs="validInputs"
        :is-editing="true"
        @update:valid-inputs="newValidInputs => validInputs = newValidInputs"
      />
      <VTabs
        v-model="tab"
        class="ms-4 height-55"
        center-active
      >
        <VTab
          class="height-55"
          value="active"
        >
          <VBadge
            floating
            :content="filteredMembersActive.length"
          >
            Active
          </VBadge>
        </VTab>
        <VTab
          class="height-55"
          value="needs-to-register"
        >
          <VBadge
            floating
            :content="filteredMembersNeedToRegister.length"
          >
            Invited
          </VBadge>
        </VTab>
        <VTab
          class="height-55"
          value="pending"
        >
          <VBadge
            floating
            :content="filteredMembersPending.length"
          >
            Approve?
          </VBadge>
        </VTab>
      </VTabs>
      <VTextField
        v-if="adminEditingUserGroupMembers?.userPermissions.length>3"
        v-model="searchValue"
        density="compact"
        variant="filled"
        label="Search by name, email, etc..."
        append-inner-icon="mdi-magnify"
        single-line
        hide-details
        clearable
        class="ma-4"
      />
    </div>
    <VCard class="pt-0 ps-0 main-content">
      <VCardText class="pa-0">
        <VWindow v-model="tab">
          <VWindowItem value="active">
            <GroupMemberCardAdmin
              v-for="member in filteredMembersActive"
              class="mb-4"
              :member="member"
              :user-group="adminEditingUserGroupMembers"
              :valid-inputs="validInputs"
              @update:valid-inputs="newValidInputs => validInputs = newValidInputs"
            />
            <div class="mb-16" />
          </VWindowItem>
          <VWindowItem value="needs-to-register">
            <VCol
              v-if="!searchValue && filteredMembersNeedToRegister?.length > 1"
              class="text-center"
            >
              <VBtn @click="isConfirmRemindDialogOpen=true;">
                Remind all
              </VBtn>
            </VCol>
            <GroupMemberCardAdmin
              v-for="member in filteredMembersNeedToRegister"
              class="mb-4"
              :member="member"
              :user-group="adminEditingUserGroupMembers"
              :valid-inputs="validInputs"
              @update:valid-inputs="newValidInputs => validInputs = newValidInputs"
            />
            <div class="mb-16" />
            <div
              v-if="filteredMembersNeedToRegister?.length == 0"
              class="text-center mt-8"
            >
              All invited members have registered
            </div>
          </VWindowItem>

          <VWindowItem value="pending">
            <GroupMemberCardAdmin
              v-for="member in filteredMembersPending"
              class="mb-4"
              :member="member"
              :user-group="adminEditingUserGroupMembers"
              :valid-inputs="validInputs"
              @update:valid-inputs="newValidInputs => validInputs = newValidInputs"
            />
            <div class="mb-16" />
            <div
              v-if="filteredMembersPending?.length == 0"
              class="text-center mt-8"
            >
              No members awaiting approval
            </div>
          </VWindowItem>

          <!--          <v-window-item value="member action"> -->
          <!--            <GroupMemberCardAdmin -->
          <!--              v-for="member in filteredMembersMemberAction" -->
          <!--              :key="member.email" -->
          <!--              class="mb-4" -->
          <!--              :member="member" -->
          <!--              :userGroup="adminEditingUserGroupMembers" -->
          <!--            /> -->
          <!--          </v-window-item> -->


          <!--          <v-window-item value="admin"> -->
          <!--            <GroupMemberCardAdmin -->
          <!--              v-for="member in filteredMembersAdmins" -->
          <!--              :key="member.email" -->
          <!--              class="mb-4" -->
          <!--              :member="member" -->
          <!--              :userGroup="adminEditingUserGroupMembers" -->
          <!--            /> -->
          <!--          </v-window-item> -->

          <!--          <v-window-item value="new"> -->
          <!--            <GroupMemberCardAdmin -->
          <!--              v-for="member in filteredMembersNew" -->
          <!--              class="mb-4" -->
          <!--              :member="member" -->
          <!--              :userGroup="adminEditingUserGroupMembers" -->
          <!--            /> -->
          <!--          </v-window-item> -->
        </VWindow>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.raised-icon {
  position: relative;
  top: -4px; /* Adjust this value as needed to raise the icon */
}

.v-toolbar-title{
  flex: unset;
}

.v-tab{
  color: grey;
}

.toolbar-header{
  position: relative;

}

.info-header{
  position: relative;
  background-color: var(--ds-surface-card);
}

.main-content{
  position: relative;
  display: unset !important;
  padding-bottom: 350px
  //min-height: unset !important;
}

.bottom-buttons{
  position: sticky;
  bottom: 0;
  width:100%;
  display: unset !important;
  background-color: var(--ds-surface-card);
  text-align: right;
}

.height-55 {
  height: 55px !important;
}
</style>
